// Libraries
import { ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router';
import styled, { ThemeProvider } from 'styled-components';
// eslint-disable-next-line import/no-absolute-path, import/no-unresolved
import '/node_modules/flag-icons/css/flag-icons.min.css';

// Dependencies
import Routes from 'containers/Routes';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/pt';
import isoWeek from 'dayjs/plugin/isoWeek';
import withErrorHandler from 'hocs/withErrorHandler';
import { TranslationsProvider } from 'providers/translationsProvider';
import { getAntTheme } from 'styles/antTheme';
import 'styles/chatbox.css';
import 'styles/index.css';
import { mainTheme, mainThemeA11y } from 'styles/themes';
import { initBeforeUnload } from 'utils/beforeUnload';
import getTranslationHandlers from 'utils/translationHandlers';

// Actions and selectors
import { selectors as langSelectors } from 'store/modules/translations';
import { actions as authActions, selectors as userSelectors } from 'store/modules/user';

// TODO: This file should in in styles, not here.
import GlobalStyle from './globalStyle';

const WrapperComponent = styled.div`
  min-height: 100vh;
`;

const getPageTitle = () => {
  if (window.prod) {
    return 'Bridge for Billions';
  }

  if (window.staging) {
    return 'Bridge for Billions - Demo';
  }

  return 'Dev - Bridge for Billions';
};

const getLangCode = lang => {
  if (lang === 'pt_br') return 'pt-BR';
  return lang ? lang.split('_')[0] : 'en';
};

dayjs.extend(isoWeek);

const setLanguage = lang => {
  switch (lang) {
    case 'es_es':
      dayjs.locale('es');
      break;
    case 'fr_fr':
      dayjs.locale('fr');
      break;
    case 'pt_br':
      dayjs.locale('pt-br');
      break;
    case 'pt_pt':
      dayjs.locale('pt');
      break;
    case 'it_it':
      dayjs.locale('it');
      break;
    default:
      dayjs.locale('en-gb');
  }
};

const App = () => {
  const dispatch = useDispatch();

  const selectedLanguage = useSelector(langSelectors.getSelectedLanguage);
  const userIsAccessible = useSelector(userSelectors.userIsAccessible);
  const highContrastMode = useSelector(userSelectors.getHighContrastMode);

  const activeTheme = highContrastMode ? mainThemeA11y : mainTheme;
  const configProviderTheme = getAntTheme(highContrastMode);
  setLanguage(selectedLanguage);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const joinList = urlParams.get('join-list');
    if (joinList) {
      sessionStorage.setItem('joinList', joinList);
    }
  }, []);

  useEffect(() => {
    dispatch(authActions.userRestore());
    initBeforeUnload();
  }, []);

  const translationHandlers = getTranslationHandlers(selectedLanguage);

  const pageTitle = getPageTitle();

  return (
    <ThemeProvider theme={activeTheme}>
      <ConfigProvider theme={configProviderTheme}>
        <TranslationsProvider value={translationHandlers}>
          <GlobalStyle language={selectedLanguage} />
          <WrapperComponent>
            <Helmet htmlAttributes={{ lang: getLangCode(selectedLanguage) }} titleTemplate={`%s | ${pageTitle}`} defaultTitle={pageTitle}>
              <meta charSet="utf-8" />
            </Helmet>
            {userIsAccessible && (
              <BrowserRouter>
                <Routes />
              </BrowserRouter>
            )}
          </WrapperComponent>
        </TranslationsProvider>
      </ConfigProvider>
    </ThemeProvider>
  );
};

const WrappedApp = withErrorHandler(App);
export default WrappedApp;
